import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { exportCSV, exportPDF } from '../utils/export.helpers';
import { usePagination, SearchBar } from '../components/Pagination';

export default function RapportAbsences() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [from, setFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [absents, setAbsents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('resume');

  useEffect(() => {
    api.get('/catalog/classes').then((c) => {
      setClasses(c || []);
      if (c?.[0]) setClassId(c[0].id);
    }).catch(() => {});
  }, []);

  function load() {
    setLoading(true); setError('');
    api.get('/registry/presences-eleves').then((d) => {
      const allRecords = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      const filtered = allRecords.filter((r) =>
        (!classId || r.classId === classId) && r.date >= from && r.date <= to
      );
      const allEntries = filtered.flatMap((r) =>
        (r.entries || []).map((e) => ({ ...e, date: r.date }))
      );
      const studentMap = {};
      allEntries.forEach((e) => {
        if (!studentMap[e.studentId]) {
          studentMap[e.studentId] = { studentId: e.studentId, matricule: e.matricule || '', nom: e.name || e.studentId, absences: 0, retards: 0, excuses: 0 };
        }
        if (e.status === 'ABSENT') studentMap[e.studentId].absences++;
        if (e.status === 'RETARD') studentMap[e.studentId].retards++;
        if (e.status === 'EXCUSE') studentMap[e.studentId].excuses++;
      });
      api.get('/students').then((students) => {
        const byId = Object.fromEntries((students || []).map((s) => [s.id, s]));
        const enriched = Object.values(studentMap).map((r) => {
          const s = byId[r.studentId];
          return s ? { ...r, matricule: s.matricule, nom: `${s.lastName} ${s.firstName}` } : r;
        });
        setRows(enriched.sort((a, b) => b.absences - a.absences));
        setAbsents(allEntries.filter((e) => e.status === 'ABSENT').map((e) => {
          const s = byId[e.studentId];
          return { ...e, lastName: s?.lastName || '', firstName: s?.firstName || '', matricule: s?.matricule || '' };
        }).sort((a, b) => (b.date || '').localeCompare(a.date || '')));
      }).catch(() => {
        setRows(Object.values(studentMap).sort((a, b) => b.absences - a.absences));
      });
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }

  useEffect(() => { if (classId) load(); }, [classId]);

  const { paginated: rowsPag, search: s1, setSearch: ss1, Pagination: P1, filtered: f1 } = usePagination(rows, { pageSize: 5, searchKeys: ['nom', 'matricule'] });
  const { paginated: absPag,  search: s2, setSearch: ss2, Pagination: P2, filtered: f2 } = usePagination(absents, { pageSize: 5, searchKeys: ['lastName', 'firstName', 'matricule'] });

  const totalAbsences = rows.reduce((s, r) => s + r.absences, 0);
  const totalRetards  = rows.reduce((s, r) => s + r.retards, 0);

  function doExport() {
    exportCSV(rows.map((r) => ({ Matricule: r.matricule, Nom: r.nom, Absences: r.absences, Retards: r.retards, Excusés: r.excuses })), 'rapport_absences');
  }
  function doPDF() {
    const cls = classes.find((c) => c.id === classId);
    exportPDF(`Rapport d'absences — ${cls?.name || ''} — Du ${new Date(from).toLocaleDateString('fr-FR')} au ${new Date(to).toLocaleDateString('fr-FR')}`,
      ['Matricule', 'Nom & Prénom', 'Absences', 'Retards', 'Excusés'],
      rows.map((r) => [r.matricule, r.nom, r.absences, r.retards, r.excuses]),
      'rapport_absences'
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h1 style={{ fontSize: '1.2rem', fontWeight: 600 }}>Rapport d'absences</h1>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Analysez les absences et retards par classe et par période.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost btn-sm" onClick={doExport}>↓ Excel</button>
          <button className="btn-ghost btn-sm" onClick={doPDF}>↓ PDF</button>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, marginBottom: 16, alignItems: 'flex-end' }}>
        <div className="field" style={{ margin: 0 }}><label>Classe</label>
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">Toutes les classes</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}><label>Du</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field" style={{ margin: 0 }}><label>Au</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button className="btn btn-sm" onClick={load} style={{ height: 38 }}>Afficher</button>
      </div>

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi"><span>Total absences</span><strong style={{ color: totalAbsences > 0 ? 'var(--danger)' : 'inherit' }}>{totalAbsences}</strong></div>
        <div className="kpi gold"><span>Total retards</span><strong>{totalRetards}</strong></div>
        <div className="kpi"><span>Élèves concernés</span><strong>{rows.filter((r) => r.absences > 0).length}</strong></div>
        <div className="kpi green"><span>Élèves sans absence</span><strong>{rows.filter((r) => r.absences === 0).length}</strong></div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'resume' ? 'active' : ''}`} onClick={() => setTab('resume')}>Résumé par élève</button>
        <button className={`tab ${tab === 'detail' ? 'active' : ''}`} onClick={() => setTab('detail')}>Détail des absences</button>
      </div>

      {error && <div className="form-error">{error}</div>}

      {tab === 'resume' && (
        <div className="card">
          <div className="card-head"><h2>Résumé par élève</h2></div>
          <SearchBar value={s1} onChange={ss1} placeholder="Nom, matricule…" total={rows.length} filtered={f1.length} />
          <div className="card-body">
            {loading ? (
              <div className="loading">Chargement…</div>
            ) : f1.length === 0 ? (
              <div className="empty">Aucune donnée sur cette période.</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Matricule</th><th>Nom & Prénom</th>
                      <th className="num" style={{ color: 'var(--danger)' }}>Absences</th>
                      <th className="num" style={{ color: 'var(--warn)' }}>Retards</th>
                      <th className="num" style={{ color: 'var(--primary)' }}>Excusés</th>
                      <th style={{ width: 150 }}>Assiduité</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsPag.map((r) => {
                      const joursPeriode = Math.ceil((new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24) * 5 / 7);
                      const assiduite = joursPeriode > 0 ? Math.max(0, Math.round((1 - r.absences / joursPeriode) * 100)) : 100;
                      return (
                        <tr key={r.studentId} style={r.absences >= 5 ? { background: 'var(--danger-soft)' } : {}}>
                          <td className="mono">{r.matricule}</td>
                          <td><b>{r.nom}</b></td>
                          <td className="num" style={{ color: r.absences > 0 ? 'var(--danger)' : 'inherit', fontWeight: r.absences > 0 ? 700 : 400 }}>{r.absences}</td>
                          <td className="num" style={{ color: r.retards > 0 ? 'var(--warn)' : 'inherit' }}>{r.retards}</td>
                          <td className="num">{r.excuses}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, height: 6, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${assiduite}%`, background: assiduite >= 90 ? 'var(--success)' : assiduite >= 75 ? 'var(--warn)' : 'var(--danger)', borderRadius: 3 }} />
                              </div>
                              <span style={{ fontSize: 11, minWidth: 35 }}>{assiduite}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <P1 />
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'detail' && (
        <div className="card">
          <div className="card-head"><h2>Détail des absences</h2><span className="badge red">{absents.length}</span></div>
          <SearchBar value={s2} onChange={ss2} placeholder="Nom, date…" total={absents.length} filtered={f2.length} />
          <div className="card-body">
            {f2.length === 0 ? (
              <div className="empty">Aucune absence sur cette période.</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Date</th><th>Jour</th><th>Élève</th><th>Matricule</th><th>Statut</th><th>Motif</th></tr>
                  </thead>
                  <tbody>
                    {absPag.map((r, i) => (
                      <tr key={i}>
                        <td className="mono">{r.date ? new Date(r.date).toLocaleDateString('fr-FR') : '—'}</td>
                        <td>{r.date ? new Date(r.date).toLocaleDateString('fr-FR', { weekday: 'long' }) : '—'}</td>
                        <td><b>{r.lastName} {r.firstName}</b></td>
                        <td className="mono">{r.matricule}</td>
                        <td><span className="badge red">Absent</span></td>
                        <td>{r.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <P2 />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
